import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Edit2,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import "./ProductScheme.css";

const roiFrequencies = ["MONTHLY", "QUARTERLY", "HALF-YEARLY", "YEARLY", "WEEKLY", "DAILY"];
const tenureTypes = ["MONTH", "QUARTER", "HALF-YEAR", "YEAR", "WEEK", "DAY"];
const recoveryFrequencys = [
  "MONTHLY",
  "QUARTERLY",
  "HALF-YEARLY",
  "YEARLY",
  "WEEKLY",
  "DAILY",
];

const ProductScheme = () => {
  const initialFormState = {
    serviceName: "",
    serviceType: "Product Loan",
    minAmount: "",
    maxAmount: "",
    minTenure: "",
    maxTenure: "",
    roi: "",
    roiFrequency: "MONTHLY",
    roiType: "Flat",
    prefix: "",
    tenureType: "MONTH",
    recoveryFrequency: "MONTHLY",
    loanType: "Consumer",
    securityType: "Secured",
    processingFeesType: "Rs",
    processingFeesValue: "",
    penaltyType: "Rs",
    penaltyValue: "",
    gracePeriodDays: "",
    gstPercentage: "",
    finePerPercentage: "",
    fileChargeType: "Rs",
    fileChargeValue: "",
    insuranceType: "Rs",
    insuranceValue: "",
    minAge: "",
    maxAge: "",
    schemeStatus: "ACTIVE",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  const API_URL = "https://payyug.in/api"; 
  const superAdminId = localStorage.getItem("adminuserId");

  // 1. GET ALL SCHEMES
  const fetchSchemes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/consumer-loan/scheme/list`, {
        params: {
          superAdminId,
          page: currentPage, 
          limit: itemsPerPage,
        },
      });
      if (res.data.success) {
        setSchemes(res.data.data.data || []); 
        setTotalPages(res.data.data.totalPages || 1);
        setTotalRecords(res.data.data.totalRecords || 0);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
    fetchSchemes();
  }, [currentPage, itemsPerPage, superAdminId]);

  console.log(schemes);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // 2. CREATE & UPDATE (POST / PUT)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      isFixedTenure: true,
      maxTenureLimit: formData.maxTenure,
      superAdminId,
    };
    try {
      if (isEditing) {
        const res = await axios.put(
          `${API_URL}/consumer-loan/scheme/${formData.id}`,
          payload,
        );
        if (res.data.success)
          toast.success(res.data.message || "Scheme Updated Successfully!");
      } else {
        const res = await axios.post(
          `${API_URL}/consumer-loan/scheme/create`,
          payload,
        );
        if (res.data.success)
          toast.success(res.data.message || "New Scheme Created!");
      }
      handleReset();
      fetchSchemes();
    } catch (err) {
      toast.error(err.response?.data?.message + " " + err.response.data.error || "Something went wrong");
      // toast.error(err.response.data.error)
    }
  };

  // 3. DELETE
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this scheme?")) {
      try {
        const res = await axios.delete(`${API_URL}/consumer-loan/scheme/${id}`);
        if (res.data.success) {
          toast.success(res.data.message || "Scheme Deleted!");
          fetchSchemes();
        }
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setIsEditing(false);
  };

  // Toggle Function for Form
  const handleStatusToggle = () => {
    setFormData((prev) => ({
      ...prev,
      schemeStatus: prev.schemeStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE",
    }));
  };

const filteredItems = schemes.filter((s) =>
    (s.serviceName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.prefix || "").toLowerCase().includes(searchTerm.toLowerCase())
  );


  // 3. PAGINATION HANDLERS
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Limit badalne par pehle page par reset karein
  };

  return (
    <div className="product-loan-scheme-container">
      <div className="product-loan-scheme-header-card">
        <h2>{isEditing ? "Edit Scheme" : "Create New Scheme"}</h2>
        <button className="reset-btn" onClick={handleReset}>
          <RefreshCw size={14} /> Reset
        </button>
      </div>

      <div className="product-loan-scheme-form-card">
        <form onSubmit={handleSubmit}>
          <div className="product-loan-scheme-form-grid">
            {[
              { label: "Service Type", name: "serviceType", type: "select", options: ["Product Loan"] },
    { label: "Service Name", name: "serviceName", type: "input", required: true },
    { label: "Prefix (e.g., PL)", name: "prefix", type: "input", required: true },
    { label: "Loan Type", name: "loanType", type: "select", options: ["Consumer", "Personal", "Business"] },
    { label: "Min Amount", name: "minAmount", type: "number" },
    { label: "Max Amount", name: "maxAmount", type: "number" },
    { label: "ROI (%)", name: "roi", type: "number", step: "0.01" },
    { label: "ROI Frequency", name: "roiFrequency", type: "select", options: roiFrequencies },
    { label: "ROI Type", name: "roiType", type: "select", options: ["Flat", "Reducing"] },
    { label: "Tenure Type", name: "tenureType", type: "select", options: tenureTypes },
    { label: "Min Tenure", name: "minTenure", type: "number" },
    { label: "Max Tenure", name: "maxTenure", type: "number" },
    { label: "Recovery Frequency", name: "recoveryFrequency", type: "select", options: recoveryFrequencys },
    { label: "Processing Fees Type", name: "processingFeesType", type: "select", options: ["Rs", "Percent"] },
    { 
      label: `Processing Fees Value ${formData.processingFeesType.toLowerCase() === "percent" ? "(%)" : "(Rs)"}`, 
      name: "processingFeesValue", 
      type: "number" 
    },
    { label: "File Charge Type", name: "fileChargeType", type: "select", options: ["Rs", "Percent"] },
    { 
      label: `File Charge Value ${formData.fileChargeType.toLowerCase() === "percent" ? "(%)" : "(Rs)"}`, 
      name: "fileChargeValue", 
      type: "number" 
    },
    { label: "Penalty Type", name: "penaltyType", type: "select", options: ["Rs", "Percent"] },
    { 
      label: `Penalty Value ${formData.penaltyType.toLowerCase() === "percent" ? "(%)" : "(Rs)"}`, 
      name: "penaltyValue", 
      type: "number" 
    },
    { label: "Insurance Type", name: "insuranceType", type: "select", options: ["Rs", "Percent"] },
    { 
      label: `Insurance Value ${formData.insuranceType.toLowerCase() === "percent" ? "(%)" : "(Rs)"}`, 
      name: "insuranceValue", 
      type: "number" 
    },
    { label: "Grace Period (Days)", name: "gracePeriodDays", type: "number" },
    { label: "Gst (%)", name: "gstPercentage", type: "number" },
    { label: "Fine Per (%)", name: "finePerPercentage", type: "number" },
    { label: "Security Type", name: "securityType", type: "select", options: ["Secured", "UnSecured"] },
    { label: "Min Age", name: "minAge", type: "number" },
    { label: "Max Age", name: "maxAge", type: "number" }
            ].map((field) => (
            <div key={field.name} className="product-loan-scheme-form-input-group">
              <label>{field.label}</label>
              
              {field.type === "select" ? (
                <select
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required={field.required}
                >
                  {/* <option value="">Select</option> */}
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  step={field.step}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required={field.required}
                />
              )}
            </div>
          ))}
            <div className="product-loan-scheme-form-input-group">
              <label>Scheme Status</label>
              <div
                className="status-toggle-wrapper"
                onClick={handleStatusToggle}
              >
                <div
                  className={`status-toggle-slider ${formData.schemeStatus === "ACTIVE" ? "active" : ""}`}
                >
                  <div className="status-toggle-knob"></div>
                </div>
                <span
                  className={`status-toggle-text ${formData.schemeStatus === "ACTIVE" ? "text-active" : "text-inactive"}`}
                >
                  {formData.schemeStatus}
                </span>
              </div>{" "}
            </div>
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="submit"
              className="product-loan-scheme-form-isubmin-btn"
            >
              {isEditing ? "Update Scheme" : "Create Scheme"}
            </button>
          </div>
        </form>
      </div>

      <div className="product-loan-scheme-table-container">
        <div className="product-loan-scheme-table-controles">
          <div>
            Show{" "}
           <select value={itemsPerPage} onChange={handleLimitChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select> entries
          </div>
          <div
            className="product-loan-scheme-table-search-box"
            style={{ position: "relative" }}
          >
            <Search size={16} style={{ position: "absolute", left: "10px", top: "10px", color: "#999" }} />
            <input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "35px" }}
            />
          </div>
        </div>

<div className="product-loan-scheme-report-container">
        <table className="product-loan-scheme-table-report">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Service Type</th>
              <th>Service Name</th>
              <th>Prefix</th>
              <th>Minimum Amount</th>
              <th>Maximum Amount</th>
              <th>ROI (Roi Frequency)</th>
              <th>Tenure Type</th>
              <th>Min Tenure</th>
              <th>Max Tenure</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  Loading Records...
                </td>
              </tr>
            ) : filteredItems.length > 0 ? (
              filteredItems.map((s,index) => (
                <tr key={s.id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{s.serviceType||"N/A"}</td>
                  <td style={{ fontWeight: "600" }}>{s.serviceName||"N/A"}</td>
                  <td>{s.prefix||"N/A"}</td>
                  <td>{s.minAmount||"N/A"}</td>
                  <td>{s.maxAmount||"N/A"}</td>
                  <td>
                    {`${s.roi}% (${s.roiFrequency})`||"N/A"}
                  </td>
                  <td>{s.tenureType||"N/A"}</td>
                  <td>
                    {s.minTenure||"N/A"}
                  </td>
                  <td>
                    {s.maxTenure||"N/A"}
                  </td>
                  <td>
                    {s.schemeStatus||"N/A"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="btn-edit"
                      onClick={() => {
                        setFormData(s);
                        setIsEditing(true);
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(s.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

       <div className="product-loan-scheme-pagination">
          <div className="pagination-info">
            Page <strong>{currentPage}</strong> of {totalPages} ({totalRecords} total items)
          </div>
          <div className="pagination-buttons">
            <button 
              className="product-loan-scheme-pagination-btn"
              disabled={currentPage === 1 || loading}
              onClick={handlePrev}
            >
              <ChevronLeft size={14} /> Previous
            </button>
            <button 
              className="product-loan-scheme-pagination-btn"
              disabled={currentPage >= totalPages || loading}
              onClick={handleNext}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductScheme;
